import { Footer } from "@/components/footer"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { MapPin, Mail, Phone } from "lucide-react"
import { SEO } from "@/components/SEO"

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Contact Us - StayApp | Get in Touch with Our Team"
        description="Contact StayApp for inquiries, partnership opportunities, or support. Reach us via email at info@stayapp.com, phone at +1 (555) 123-4567, or visit our office."
        keywords="contact StayApp, customer support, business inquiries, partnership, StayApp contact"
        ogImage="/logo.png"
        ogUrl="https://stayapp.com/contact"
        canonical="https://stayapp.com/contact"
      />
      <main className="pt-28 md:pt-36 lg:pt-40">
      {/* Hero Section */}
      <section className="bg-black text-white py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 text-[#75055B] px-2">Get In Touch</h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 sm:gap-16 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-black mb-4 sm:mb-6">Send Us a Message</h2>
              <form className="space-y-5 sm:space-y-6">
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                    <Input placeholder="John" className="text-sm sm:text-base" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <Input placeholder="Doe" className="text-sm sm:text-base" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <Input type="email" placeholder="john@example.com" className="text-sm sm:text-base" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <Input placeholder="How can we help?" className="text-sm sm:text-base" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <Textarea
                    placeholder="Tell us more about your inquiry..."
                    className="min-h-[150px] text-sm sm:text-base"
                  />
                </div>
                <Button
                  size="lg"
                  className="w-full bg-[#75055B] text-white hover:bg-[#DB4789] hover:shadow-lg hover:shadow-[#DB4789]/30 text-sm sm:text-base"
                >
                  Send Message
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-black mb-4 sm:mb-6">Contact Information</h2>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                Feel free to reach out through any of these channels. We're here to help!
              </p>

              <div className="space-y-5 sm:space-y-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#75055B]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#75055B]" />
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <h3 className="font-semibold text-black mb-1 text-sm sm:text-base">Office Location</h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      123 Innovation Drive
                      <br />
                      Tech City, TC 12345
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#75055B]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-[#75055B]" />
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <h3 className="font-semibold text-black mb-1 text-sm sm:text-base">Email</h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      General: info@stayapp.com
                      <br />
                      Support: support@stayapp.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#75055B]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-[#75055B]" />
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <h3 className="font-semibold text-black mb-1 text-sm sm:text-base">Phone</h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Main: +1 (555) 123-4567
                      <br />
                      Support: +1 (555) 987-6543
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-8 sm:mt-10 p-5 sm:p-6 rounded-xl bg-[#75055B]/5 border border-[#75055B]/20">
                <h3 className="font-semibold text-black mb-2 sm:mb-3 text-sm sm:text-base">Business Hours</h3>
                <div className="space-y-2 text-xs sm:text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span className="font-medium">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span className="font-medium">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span className="font-medium">Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
    </>
  )
}

